// Multi-Depot Manager — manage depot locations and contacts
import { useState, useCallback } from "react";
import {
  Building,
  Plus,
  Pencil,
  Phone,
  MapPin,
  User,
  AlertCircle,
} from "lucide-react";
import { Button, Skeleton } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useDepots,
  useCreateDepot,
  useUpdateDepot,
} from "@/hooks/admin/useDepots";
import type { Depot, DepotPayload } from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SENEGAL_REGIONS = [
  "Dakar",
  "Kaolack",
  "Saint-Louis",
  "Fatick",
  "Thiès-Mbour",
  "Diourbel-Touba",
] as const;

const EMPTY_FORM: DepotPayload = {
  name: "",
  address: "",
  region: "Dakar",
  phone: "",
  manager_name: "",
  is_active: true,
};

// ---------------------------------------------------------------------------
// Depot form modal
// ---------------------------------------------------------------------------

interface DepotFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly depot: Depot | null;
  readonly onSave: (payload: DepotPayload) => Promise<void>;
  readonly isSaving: boolean;
}

function DepotFormModal({
  open,
  onClose,
  depot,
  onSave,
  isSaving,
}: DepotFormModalProps) {
  const [form, setForm] = useState<DepotPayload>(
    depot
      ? {
          name: depot.name,
          address: depot.address,
          region: depot.region,
          phone: depot.phone,
          manager_name: depot.manager_name,
          is_active: depot.is_active,
        }
      : EMPTY_FORM,
  );
  const [formError, setFormError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setFormError(null);
    onClose();
  }, [onClose]);

  const updateField = useCallback(
    <K extends keyof DepotPayload>(key: K, value: DepotPayload[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) {
      setFormError("Le nom du dépôt est requis.");
      return;
    }
    if (!form.address.trim()) {
      setFormError("L'adresse est requise.");
      return;
    }
    setFormError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    }
  }, [form, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={depot ? "Modifier le dépôt" : "Nouveau dépôt"}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            {depot ? "Enregistrer" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Nom du dépôt
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
            placeholder="ex. Dépôt Dakar Nord"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Adresse
          </label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => updateField("address", e.target.value)}
            placeholder="Adresse complète"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              Région
            </label>
            <select
              value={form.region}
              onChange={(e) => updateField("region", e.target.value)}
              className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {SENEGAL_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              Téléphone
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              placeholder="+221 7X XXX XX XX"
              className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Responsable
          </label>
          <input
            type="text"
            value={form.manager_name}
            onChange={(e) => updateField("manager_name", e.target.value)}
            placeholder="Prénom Nom"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="depot-active"
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => updateField("is_active", e.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
          />
          <label
            htmlFor="depot-active"
            className="text-sm font-medium text-stone-700"
          >
            Dépôt actif
          </label>
        </div>

        {formError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Depot card component
// ---------------------------------------------------------------------------

interface DepotCardProps {
  readonly depot: Depot;
  readonly onEdit: () => void;
  readonly onToggle: () => void;
}

function DepotCard({ depot, onEdit, onToggle }: DepotCardProps) {
  return (
    <div className="flex flex-col rounded-lg border border-stone-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100">
          <Building className="h-5 w-5 text-stone-500" />
        </div>
        <button
          type="button"
          onClick={onToggle}
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
            depot.is_active
              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              : "bg-stone-100 text-stone-500 hover:bg-stone-200"
          }`}
        >
          {depot.is_active ? "Actif" : "Inactif"}
        </button>
      </div>

      {/* Name + region */}
      <div className="mt-3">
        <h3 className="font-semibold text-stone-900">{depot.name}</h3>
        <span className="mt-0.5 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
          {depot.region}
        </span>
      </div>

      {/* Details */}
      <div className="mt-3 space-y-1.5 text-sm text-stone-600">
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
          <span className="line-clamp-2">{depot.address}</span>
        </div>
        {depot.phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 shrink-0 text-stone-400" />
            <span>{depot.phone}</span>
          </div>
        )}
        {depot.manager_name && (
          <div className="flex items-center gap-2">
            <User className="h-3.5 w-3.5 shrink-0 text-stone-400" />
            <span>{depot.manager_name}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 border-t border-stone-100 pt-3">
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700"
        >
          <Pencil className="h-3.5 w-3.5" />
          Modifier
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Depots() {
  const { data, isLoading, isError, refetch } = useDepots();
  const createDepot = useCreateDepot();
  const updateDepot = useUpdateDepot();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingDepot, setEditingDepot] = useState<Depot | null>(null);

  const depots = data?.depots ?? [];

  const handleCreateSave = useCallback(
    async (payload: DepotPayload) => {
      await createDepot.mutateAsync(payload);
      setIsCreateOpen(false);
    },
    [createDepot],
  );

  const handleEditSave = useCallback(
    async (payload: DepotPayload) => {
      if (!editingDepot) return;
      await updateDepot.mutateAsync({ id: editingDepot.id, payload });
      setEditingDepot(null);
    },
    [editingDepot, updateDepot],
  );

  const handleToggle = useCallback(
    (depot: Depot) => {
      updateDepot.mutate({ id: depot.id, payload: { is_active: !depot.is_active } });
    },
    [updateDepot],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Building className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Dépôts
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${depots.length} dépôt(s) configuré(s)`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouveau dépôt
          </Button>
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les dépôts.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-lg border border-stone-200 bg-white p-5"
              >
                <Skeleton className="mb-3 h-10 w-10 rounded-lg" />
                <Skeleton className="mb-2 h-5 w-36" />
                <Skeleton className="mb-1 h-3 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && depots.length === 0 && (
          <EmptyState
            icon={<Building className="h-6 w-6" />}
            title="Aucun dépôt configuré"
            description="Créez votre premier dépôt pour organiser vos livraisons par zone."
            action={{ label: "Nouveau dépôt", onClick: () => setIsCreateOpen(true) }}
          />
        )}

        {/* Depot cards grid */}
        {!isLoading && !isError && depots.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {depots.map((depot) => (
              <DepotCard
                key={depot.id}
                depot={depot}
                onEdit={() => setEditingDepot(depot)}
                onToggle={() => handleToggle(depot)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      <DepotFormModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        depot={null}
        onSave={handleCreateSave}
        isSaving={createDepot.isPending}
      />

      {/* Edit modal */}
      <DepotFormModal
        open={editingDepot !== null}
        onClose={() => setEditingDepot(null)}
        depot={editingDepot}
        onSave={handleEditSave}
        isSaving={updateDepot.isPending}
      />
    </AdminLayout>
  );
}
